// @tennis-matches/services/location
// Kalendertag (YYYY-MM-DD) in einer IANA-Zeitzone — zentrale Intl-Kapselung.

import { LOCATION_FALLBACK_TIMEZONE } from "./location.service.js";

export { LOCATION_FALLBACK_TIMEZONE };

/**
 * Prüft, ob `timeZone` von Intl akzeptiert wird.
 * @param {string} timeZone
 * @returns {boolean}
 */
export function isValidIanaTimeZone(timeZone) {
  const tz = String(timeZone ?? "").trim();
  if (!tz) return false;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: tz }).format(new Date());
    return true;
  } catch {
    return false;
  }
}

/**
 * Fachliche Tenant-/Vereins-Zeitzone. Einziger Fallback: LOCATION_FALLBACK_TIMEZONE.
 * @param {{ timeZone?: string, timezone?: string }|string|null|undefined} input
 * @returns {string} IANA-Timezone
 */
export function resolveTenantCalendarTimeZone(input) {
  const raw =
    typeof input === "string"
      ? String(input).trim()
      : String(input?.timeZone ?? input?.timezone ?? "").trim();
  if (raw && isValidIanaTimeZone(raw)) return raw;
  return LOCATION_FALLBACK_TIMEZONE;
}

/**
 * Lokaler Kalendertag (YYYY-MM-DD) für einen Instant in einer IANA-Zeitzone.
 * @param {Date|number|string} [instant]
 * @param {string} [timeZone]
 * @returns {string}
 */
export function calendarYmdInTimeZone(instant, timeZone) {
  const tz = resolveTenantCalendarTimeZone(timeZone);
  const date =
    instant instanceof Date
      ? instant
      : instant == null || instant === ""
        ? new Date()
        : new Date(instant);
  if (!Number.isFinite(date.getTime())) {
    return calendarYmdInTimeZone(new Date(), tz);
  }
  try {
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone: tz,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(date);
    const y = parts.find((p) => p.type === "year")?.value;
    const m = parts.find((p) => p.type === "month")?.value;
    const d = parts.find((p) => p.type === "day")?.value;
    if (y && m && d) return `${y}-${m}-${d}`;
  } catch {
    // Fallback nur über resolveTenantCalendarTimeZone bereits abgesichert
  }
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: LOCATION_FALLBACK_TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(date);
  const y = parts.find((p) => p.type === "year")?.value;
  const m = parts.find((p) => p.type === "month")?.value;
  const d = parts.find((p) => p.type === "day")?.value;
  return y && m && d ? `${y}-${m}-${d}` : "";
}

/**
 * Vereins-Kalendertag „heute“ (oder für übergebenen Instant).
 * @param {{ now?: Date|number|string, timeZone?: string, timezone?: string }|Date|number|string|null|undefined} [input]
 * @returns {string} YYYY-MM-DD
 */
export function resolveTenantCalendarYmd(input) {
  if (
    input instanceof Date ||
    typeof input === "number" ||
    typeof input === "string"
  ) {
    return calendarYmdInTimeZone(input, LOCATION_FALLBACK_TIMEZONE);
  }
  const opts = input && typeof input === "object" ? input : {};
  const tz = resolveTenantCalendarTimeZone(opts);
  return calendarYmdInTimeZone(opts.now, tz);
}
